import { useCallback, useEffect, useState } from 'react'
import { sendEvent } from '@/lib/logger'
import { supabase } from '@/lib/supabase'
import type { Friend } from '@/types/booky'

export const useFriends = (userId: string | undefined) => {
  const [friends, setFriends] = useState<Friend[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFriends = useCallback(async () => {
    if (!userId) {
      setFriends([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error } = await supabase
        .from('booky_friends')
        .select('id, name, notes, created_at')
        .order('name', { ascending: true })

      if (error) {
        throw error
      }

      setFriends((data ?? []) as Friend[])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load friends'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    void fetchFriends()
  }, [fetchFriends])

  const addFriend = async (name: string, notes?: string) => {
    if (!userId) {
      return { data: null, error: new Error('User not signed in') }
    }

    const { data, error } = await supabase
      .from('booky_friends')
      .insert({
        user_id: userId,
        name,
        notes: notes?.trim() ? notes.trim() : null,
      })
      .select('id, name, notes, created_at')
      .single()

    if (!error) {
      sendEvent('friend.created', 'Friend created', 'INFO', {
        metadata: {
          friendId: data?.id,
          friendName: data?.name,
        },
      })
      await fetchFriends()
    }

    return { data: (data as Friend | null) ?? null, error }
  }

  const removeFriend = async (friendId: number) => {
    const { error } = await supabase.from('booky_friends').delete().eq('id', friendId)

    if (!error) {
      await fetchFriends()
    }

    return { error }
  }

  return {
    friends,
    loading,
    error,
    fetchFriends,
    addFriend,
    removeFriend,
  }
}
